<template>
    <th
        :class="[style['mds-data-table__header-cell'], classObject]"
        :style="{ width: width }"
        :aria-sort="ariaSort || false"
        scope="col"
    >
        <div :class="style['mds-data-table__header-cell-inner']">
            <template v-if="sortable">
                <button
                    :class="style['mds-data-table__header-cell-button']"
                    @click="emitSortFieldChange"
                    v-html="text || fieldName"
                    tabindex="0"
                    type="button"
                >
                    <slot />
                </button>
            </template>
            <span :class="style['mds-data-table__header-cell-text']" v-else v-html="text || fieldName">
                <slot />
            </span>
            <span v-if="actions || actionItems" :class="[style['mds-data-table__header-cell-action']]">
                <slot name="action">
                    <mds-button
                        v-for="(button, index) in actionItems"
                        ref="action"
                        :key="index"
                        variation="icon-only"
                        :id="button.id"
                        :text="text"
                        :icon="button.icon"
                        size="small"
                        @click="emitHeaderActionClicked(button.id, $event)"
                    />
                </slot>
            </span>
        </div>
    </th>
</template>

<script>
    import { MdsButton } from '@mds/button';

    export default {
        name: 'MdsDataTableHeaderCell',
        components: {
            MdsButton,
        },
        props: {
            actions: {
                type: Boolean,
                docs: {
                    validation: '—',
                    description: 'If `true`, allows action items to be passed via action-cell slot.',
                },
            },
            actionItems: {
                type: Array,
                docs: {
                    validation: '—',
                    description: 'Set of objects with `icon` and `id` values for icon-only buttons placed in header.',
                },
            },
            align: {
                type: String,
                default: 'left',
                validator(value) {
                    return ['left', 'right'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `left`, `right`.',
                    description: 'Sets the column content alignment.',
                },
            },
            fieldName: {
                type: String,
                required: true,
                docs: {
                    validation: '—',
                    description: 'Sets the name for data mapping.',
                },
            },
            text: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the column header text.',
                    support: ['v-html'],
                },
            },
            transform: {
                type: Function,
                default: undefined,
                docs: {
                    validation: '—',
                    description: 'Function applied to cell content in column.',
                },
            },
            sortable: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, allows table to sort when header item clicked.',
                },
            },
            sorted: {
                type: Number,
                default: 0,
                validator(value) {
                    return [-1, 0, 1].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `-1`, `0`, `1`.',
                    description:
                        'Sets the default sorted column (-1: descending, 0: none, 1: ascending) and defines the sort order for that column. `sortable` must be true.',
                },
            },
            width: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the column width, should end in `px`.',
                },
            },
        },
        inject: ['headerVariation'],
        computed: {
            classObject() {
                const obj = {};
                obj[this.style['mds-data-table__header-cell--sort']] = this.sortable && this.sortOrder === 0;
                obj[this.style['mds-data-table__header-cell--sorted-ascending']] =
                    this.sortable && this.sortField && this.sortOrder === 1;
                obj[this.style['mds-data-table__header-cell--sorted-descending']] =
                    this.sortable && this.sortField && this.sortOrder === -1;
                obj[this.style['mds-data-table__cell--right']] = this.align === 'right';

                return obj;
            },
            ariaSort() {
                let ariaSort = '';

                if (this.sortable) {
                    if (this.sortField && this.sortOrder === 1) {
                        ariaSort = 'ascending';
                    } else if (this.sortField && this.sortOrder === -1) {
                        ariaSort = 'descending';
                    } else {
                        ariaSort = 'none';
                    }
                }

                return ariaSort;
            },
        },
        data() {
            return {
                height: null,
                sortOrder: 0,
                sortField: false,
                style: this.headerVariation.style,
            };
        },
        methods: {
            emitHeaderActionClicked(id, event) {
                const currentID = id ? id : null;
                const obj = {
                    id: currentID,
                    event,
                };
                this.$emit('mds-data-table-header-action-click', obj);
            },
            emitSortFieldChange() {
                if (this.sortable) {
                    this.sortField = true;
                    this.$emit('mds-data-table-sort-change', this.fieldName);
                }
            },
        },
        mounted() {
            if (this.sortable && this.sorted !== 0) {
                this.sortOrder = this.sorted;
                this.emitSortFieldChange();
            }
        },
    };
</script>
